import {
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { showError } from 'src/toasts/toasts';
import { ByWhenSelector, SelectionMode } from './ByWhenSelector';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const frequencyOptions = [
  { value: 'One-off', label: 'One-off' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Fortnightly', label: 'Fortnightly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Yearly', label: 'Yearly' },
];

function ModalScrollArea(props: React.ComponentProps<typeof ScrollArea.Autosize>) {
  return (
    <ScrollArea.Autosize
      {...props}
      type="scroll"
      scrollbarSize={8}
      scrollbars="y"
      styles={{ scrollbar: { '&[data-orientation="horizontal"]': { display: 'none' } } }}
    />
  );
}

export type ObjectiveFormValues = {
  id?: string;
  description: string;
  amount: number | null;
  amount_frequency: string | null;
  target_date: string | null;
};

export type ObjectiveSubmitData = {
  objective: ObjectiveFormValues;
  newDateOfBirth?: string;
};

type AddObjectiveModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: ObjectiveSubmitData) => Promise<void>;
  initialValues?: ObjectiveFormValues;
  clientDateOfBirth?: string | null;
};

const defaultValues: ObjectiveFormValues = {
  id: undefined,
  description: '',
  amount: null,
  amount_frequency: null,
  target_date: null,
};

export function AddObjectiveModal({
  opened,
  onClose,
  onSubmit,
  initialValues,
  clientDateOfBirth,
}: AddObjectiveModalProps) {
  const isEditing = !!initialValues?.id;
  const [localDateOfBirth, setLocalDateOfBirth] = useState<string | null>(
    clientDateOfBirth ?? null,
  );
  const [byWhenMode, setByWhenMode] = useState<SelectionMode>(
    initialValues?.target_date ? 'date' : null,
  );

  useEffect(() => {
    setLocalDateOfBirth(clientDateOfBirth ?? null);
  }, [clientDateOfBirth, opened]);

  const form = useForm<ObjectiveFormValues>({
    initialValues: initialValues || defaultValues,
    validate: {
      description: value => (!value?.trim() ? 'Description is required' : null),
    },
    validateInputOnBlur: true,
  });

  // Sync form values and mode when modal opens (for edit mode)
  useEffect(() => {
    if (opened) {
      form.setValues(initialValues || defaultValues);
      form.resetDirty(initialValues || defaultValues);
      setByWhenMode(initialValues?.target_date ? 'date' : null);
    }
  }, [opened, initialValues?.id]);

  const handleSubmit = async (values: ObjectiveFormValues) => {
    try {
      const dobChanged = localDateOfBirth !== clientDateOfBirth && localDateOfBirth !== null;
      await onSubmit({
        objective: {
          ...values,
          id: initialValues?.id,
        },
        newDateOfBirth: dobChanged ? localDateOfBirth : undefined,
      });
    } catch {
      showError('Failed to save objective');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Edit objective' : 'Add objective'}
      size={514}
      scrollAreaComponent={ModalScrollArea}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Textarea
            label={
              <Group justify="space-between" w="100%">
                <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.8">
                  Description{' '}
                  <Text span c="orange.6">
                    *
                  </Text>
                </Text>
                <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.6">
                  {form.values.description.length}/500
                </Text>
              </Group>
            }
            styles={{
              label: { width: '100%' },
              input: {
                borderRadius: 'var(--mantine-radius-md)',
                fontSize: 'var(--mantine-font-size-sm)',
                fontWeight: FONT_WEIGHTS.medium,
                color: 'var(--mantine-color-gray-10)',
                '&::placeholder': {
                  color: 'var(--mantine-color-gray-6)',
                },
              },
            }}
            placeholder="e.g., retire at 60 with £30k annual income"
            maxLength={500}
            minRows={4}
            autosize
            {...form.getInputProps('description')}
          />

          <Group grow>
            <NumberInput
              label={
                <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.8">
                  Amount
                </Text>
              }
              styles={{
                input: {
                  borderRadius: 'var(--mantine-radius-md)',
                  fontSize: 'var(--mantine-font-size-sm)',
                  fontWeight: FONT_WEIGHTS.medium,
                  color: 'var(--mantine-color-gray-10)',
                  '&::placeholder': {
                    color: 'var(--mantine-color-gray-6)',
                  },
                },
              }}
              placeholder="0.00"
              leftSection="£"
              hideControls
              thousandSeparator=","
              min={0}
              value={form.values.amount ?? ''}
              onChange={value =>
                form.setFieldValue('amount', typeof value === 'number' ? value : null)
              }
            />
            <Select
              label={
                <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.8">
                  Amount frequency
                </Text>
              }
              styles={{
                input: {
                  borderRadius: 'var(--mantine-radius-md)',
                  fontSize: 'var(--mantine-font-size-sm)',
                  fontWeight: FONT_WEIGHTS.medium,
                  color: 'var(--mantine-color-gray-10)',
                  '&::placeholder': {
                    color: 'var(--mantine-color-gray-6)',
                  },
                },
              }}
              placeholder="Select"
              data={frequencyOptions}
              {...form.getInputProps('amount_frequency')}
              clearable
            />
          </Group>

          <Box mt={24}>
            <ByWhenSelector
              value={form.values.target_date}
              onChange={date => form.setFieldValue('target_date', date)}
              clientDateOfBirth={localDateOfBirth}
              onDateOfBirthChange={setLocalDateOfBirth}
              mode={byWhenMode}
              onModeChange={setByWhenMode}
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            mt="md"
            disabled={
              !form.values.description.trim() || (byWhenMode !== null && !form.values.target_date)
            }
            styles={{
              root: { borderRadius: 'var(--mantine-radius-xl)' },
            }}
          >
            {isEditing ? 'Save objective' : 'Add objective'}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}
